import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {



  useGetServerOptionsQuery,
  useGetServerNextOptionsQuery,
  useGetGbPackagesQuery,
  usePurchaseGbPackageMutation,
  useGbPackageActionMutation,
  useGbProxyActionMutation,
} from "../../../store/api/proxiesApi";
import { ButtonSpinner, Loader } from "../../ui/Loader";

const formatTrafficMb = (usedGb?: number | null, limitGb?: number | null) => {
  const toMb = (gb: number) => (Number.isFinite(gb) && gb > 0 ? gb * 1024 : 0);
  const used = toMb(Number(usedGb || 0));
  const limit = toMb(Number(limitGb || 0));
  const u = used < 10 ? used.toFixed(3) : used.toFixed(2);
  const l = limit < 10 ? limit.toFixed(3) : limit.toFixed(2);
  return `${u} / ${l} MB`;
};


const SERVER_ID = 5;
const flag = (code: string) => {
  const c = String(code || "").toUpperCase();
  if (!/^[A-Z]{2}$/.test(c)) return "🌐";
  return String.fromCodePoint(...[...c].map((ch) => 127397 + ch.charCodeAt(0)));
};

type Hub = "home" | "buy" | "packages" | "package" | "create" | "proxies" | "proxy";
type CreateStep = "country" | "region" | "city" | "isp" | "rotation" | "review";

export function Server5Wizard() {
  const { t } = useTranslation();
  const [hub, setHub] = useState<Hub>("home");
  const [tariffId, setTariffId] = useState<number | null>(null);
  const [title, setTitle] = useState("");
  const [packageId, setPackageId] = useState<string | null>(null);
  const [proxyId, setProxyId] = useState<string | null>(null);
  const [topUpMode, setTopUpMode] = useState(false);
  const [step, setStep] = useState<CreateStep>("country");
  const [country, setCountry] = useState("");
  const [countryName, setCountryName] = useState("");
  const [region, setRegion] = useState("");
  const [city, setCity] = useState("");
  const [isp, setIsp] = useState("");
  const [rotation, setRotation] = useState(-1);
  const [q, setQ] = useState("");
  const [proxyTitle, setProxyTitle] = useState("");
  const [proxyRotation, setProxyRotation] = useState<number>(-1);

  const { data, isLoading, isError, refetch: refetchOptions } = useGetServerOptionsQuery(SERVER_ID);
  const { data: packagesData, refetch: refetchPackages, isFetching: loadingPackages } = useGetGbPackagesQuery(undefined, { skip: hub === "home" || hub === "buy" ? false : false });
  const options = (data as any)?.data?.options || (data as any)?.options || {};
  const tariffs = (options.tariffs as Array<{ id: number; title: string; trafficGb: number; price: number }>) || [];
  const countries = (options.countries as Array<{ code: string; name: string }>) || [];
  const rotations = (options.rotations as Array<{ value: number; label: string }>) || [];
  const packages = ((packagesData as any)?.data?.packages || []) as any[];
  const activePackages = packages.filter((p) => p.status === "active");
  const currentPackage = activePackages.find((p) => String(p._id) === String(packageId));

  const nextParams = useMemo(() => {
    if (hub !== "create") return null;
    if (step === "region" && country) return { step: "regions", country };
    if (step === "city" && country && region) return { step: "cities", country, region };
    if (step === "isp" && country && region && city) return { step: "isps", country, region, city };
    return null;
  }, [hub, step, country, region, city]);
  const { data: nextData, isFetching } = useGetServerNextOptionsQuery(
    { serverId: SERVER_ID, params: Object.fromEntries(Object.entries(nextParams || {}).map(([k, v]) => [k, String(v)])) },
    { skip: !nextParams },
  );
  const nextOptions = (nextData as any)?.data?.options || (nextData as any)?.options || {};
  const list = (step === "region" ? nextOptions.regions : step === "city" ? nextOptions.cities : nextOptions.isps) || [];

  const [buyPackage, { isLoading: buying }] = usePurchaseGbPackageMutation();
  const [packageAction, { isLoading: acting }] = useGbPackageActionMutation();
  const [proxyAction, { isLoading: proxyActing }] = useGbProxyActionMutation();
  const [packageProxies, setPackageProxies] = useState<any[]>([]);
  const [currentProxy, setCurrentProxy] = useState<any>(null);

  if (isLoading) return <div className="flex justify-center py-12"><Loader /></div>;
  if (isError) return <p className="text-center text-neutral-medium py-12">Residential (GB) is unavailable right now.</p>;

  const filteredCountries = countries.filter((c) => !q || c.name.toLowerCase().includes(q.toLowerCase()) || c.code.toLowerCase().includes(q.toLowerCase()));
  const filteredList = (list as string[]).filter((n) => !q || String(n).toLowerCase().includes(q.toLowerCase()));

  const openPackage = async (id: string) => {
    setPackageId(id);
    setHub("package");
  };
  const loadProxies = async (id: string) => {
    try {
      const res = await packageAction({ packageId: id, action: "proxies", method: "GET" }).unwrap();
      setPackageProxies((res as any)?.data?.proxies || []);
      setPackageId(id);
      setHub("proxies");
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to load proxies");
    }
  };

  const openProxyDetails = async (p: any) => {
    setProxyId(String(p._id));
    setProxyTitle(p.providerName || "");
    setProxyRotation(Number(p.gbRotation ?? -1));
    setCurrentProxy(p);
    setHub("proxy");
    try {
      const res = await proxyAction({ proxyId: String(p._id), action: "refresh", body: {} }).unwrap();
      const synced = (res as any)?.data?.proxy;
      if (synced) {
        setCurrentProxy(synced);
        setProxyTitle(synced.providerName || p.providerName || "");
        setProxyRotation(Number(synced.gbRotation ?? p.gbRotation ?? -1));
      }
    } catch (_) {
      // keep cached details if provider briefly unavailable
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-2xl font-heading text-neutral-light">Residential (GB)</h2>
        {hub !== "home" && (
          <button className="text-brand-accent" onClick={() => { setHub("home"); setQ(""); setTopUpMode(false); }}>Back</button>
        )}
      </div>

      {hub === "home" && (
        <div className="grid md:grid-cols-2 gap-3">
          <button className="rounded-xl border border-neutral-dark bg-neutral-dark/40 p-5 text-left text-neutral-light hover:border-brand-accent" onClick={() => { setTopUpMode(false); setTariffId(null); setTitle(""); setHub("buy"); }}>🛒 Buy package</button>
          <button className="rounded-xl border border-neutral-dark bg-neutral-dark/40 p-5 text-left text-neutral-light hover:border-brand-accent" onClick={() => { refetchPackages(); setHub("packages"); }}>📦 My packages</button>
        </div>
      )}

      {hub === "buy" && (
        <div className="space-y-4">
          <div className="grid gap-3">
            {tariffs.map((row) => (
              <button key={row.id} className={`rounded-xl border p-4 text-left ${tariffId === row.id ? "border-brand-accent" : "border-neutral-dark"} bg-neutral-dark/40 text-neutral-light`} onClick={() => setTariffId(row.id)}>
                <div className="flex justify-between"><span>{row.title}</span><span>${Number(row.price).toFixed(2)}</span></div>
              </button>
            ))}
          </div>
          {topUpMode && (
            <p className="text-neutral-medium text-sm">Top-up will be added to the selected package.</p>
          )}
          {!topUpMode && (
            <input value={title} onChange={(e) => setTitle(e.target.value.slice(0, 40))} placeholder="Package name" className="w-full rounded-lg bg-neutral-dark/60 border border-neutral-dark px-3 py-2 text-neutral-light" />
          )}
          <button disabled={buying || !tariffId || (!topUpMode && title.trim().length < 1)} className="rounded-xl bg-brand-accent text-neutral-dark px-4 py-2 font-semibold disabled:opacity-50" onClick={async () => {
            try {
              const res = await buyPackage({ tariffId: tariffId!, title: title.trim() || "Residential GB", packageMongoId: topUpMode ? packageId : null }).unwrap();
              toast.success(topUpMode ? "Traffic added" : "Package purchased");
              await refetchPackages();
              const id = (res as any)?.data?.package?._id || packageId;
              if (id) openPackage(String(id));
              else setHub("packages");
            } catch (err: any) { toast.error(err?.data?.message || "Purchase failed"); }
          }}>{buying ? <ButtonSpinner /> : "Confirm"}</button>
        </div>
      )}

      {hub === "packages" && (
        <div className="space-y-3">
          {loadingPackages && <Loader />}
          {!loadingPackages && activePackages.length === 0 && <p className="text-neutral-medium">No active packages.</p>}
          {activePackages.map((p) => (
            <button key={p._id} className="w-full rounded-xl border border-neutral-dark bg-neutral-dark/40 p-4 text-left text-neutral-light hover:border-brand-accent" onClick={() => openPackage(String(p._id))}>
              <div className="flex justify-between gap-3"><span>{p.providerName || "GB"}</span><span>{formatTrafficMb(p.trafficUsedGb, p.trafficLimitGb)}</span></div>
            </button>
          ))}
        </div>
      )}

      {hub === "package" && currentPackage && (
        <div className="rounded-2xl border border-neutral-dark p-6 space-y-4 text-neutral-light">
          <div>
            <h3 className="text-xl font-semibold">{currentPackage.providerName}</h3>
            <p className="text-neutral-medium">{formatTrafficMb(currentPackage.trafficUsedGb, currentPackage.trafficLimitGb)} · ${Number(currentPackage.price || 0).toFixed(2)}</p>
            <p className="text-neutral-medium text-sm">Expires: {currentPackage.expiresAt ? new Date(currentPackage.expiresAt).toLocaleDateString() : "-"}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            <button className="rounded-lg border border-neutral-dark px-3 py-2" onClick={() => { (window as any).__gbMoveMode = false; setStep("country"); setCountry(""); setRegion(""); setCity(""); setIsp(""); setRotation(-1); setQ(""); setHub("create"); }}>Create proxy</button>
            <button className="rounded-lg border border-neutral-dark px-3 py-2" onClick={() => loadProxies(String(currentPackage._id))}>Package proxies</button>
            <button className="rounded-lg border border-neutral-dark px-3 py-2" onClick={async () => {
              const name = window.prompt("New package name", currentPackage.providerName || "");
              if (!name) return;
              try { await packageAction({ packageId: String(currentPackage._id), action: "rename", body: { title: name } }).unwrap(); toast.success("Renamed"); refetchPackages(); } catch (err: any) { toast.error(err?.data?.message || "Rename failed"); }
            }}>Rename</button>
            <button className="rounded-lg border border-neutral-dark px-3 py-2" onClick={() => { setTopUpMode(true); setPackageId(String(currentPackage._id)); setHub("buy"); }}>Top up</button>
            <button className="rounded-lg border border-red-500/40 px-3 py-2" onClick={async () => {
              if (!window.confirm("Cancel unused package and refund? Allowed only when no traffic has been consumed (provider-checked).")) return;
              try {
                const res = await packageAction({ packageId: String(currentPackage._id), action: "cancel" }).unwrap();
                const refundAmt = Number((res as any)?.data?.refundAmount || 0);
                toast.success(refundAmt > 0 ? `Cancelled — refunded $${refundAmt.toFixed(2)}` : "Cancelled");
                refetchPackages(); setHub("packages");
              } catch (err: any) { toast.error(err?.data?.message || "Cancel failed"); }
            }}>Cancel package</button>
            <button className="rounded-lg border border-red-500/40 px-3 py-2" onClick={async () => {
              if (!window.confirm("Delete package? Unused data packages are refunded automatically.")) return;
              try {
                const res = await packageAction({ packageId: String(currentPackage._id), action: "delete" }).unwrap();
                const refundAmt = Number((res as any)?.data?.refundAmount || 0);
                toast.success(refundAmt > 0 ? `Deleted — refunded $${refundAmt.toFixed(2)}` : "Deleted");
                refetchPackages();
                setHub("packages");
              } catch (err: any) { toast.error(err?.data?.message || "Delete failed"); }
            }}>Delete package</button>
          </div>
        </div>
      )}

      {hub === "create" && (
        <div className="space-y-4">
          {step === "country" && (
            <>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("proxies.wizard.searchCountry") || "Search country"} className="w-full rounded-lg bg-neutral-dark/60 border border-neutral-dark px-3 py-2 text-neutral-light" />
              <div className="grid grid-cols-3 gap-1.5 max-h-[36rem] overflow-auto text-sm">
                {filteredCountries.map((c) => (
                  <button key={c.code} className="rounded-lg border border-neutral-dark px-3 py-2 text-left text-neutral-light hover:border-brand-accent" onClick={() => { setCountry(c.code); setCountryName(c.name); setRegion(""); setCity(""); setIsp(""); setQ(""); setStep("region"); }}>
                    {flag(c.code)} {c.name}
                  </button>
                ))}
              </div>
            </>
          )}
          {["region", "city", "isp"].includes(step) && (
            <>
              {isFetching && <Loader />}
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="w-full rounded-lg bg-neutral-dark/60 border border-neutral-dark px-3 py-2 text-neutral-light" />
              <button className="text-brand-accent" onClick={() => { setQ(""); setStep("rotation"); }}>Any / Skip</button>
              <div className="grid grid-cols-3 gap-1.5 max-h-[36rem] overflow-auto text-sm">
                {filteredList.map((name: string) => (
                  <button key={name} className="rounded-lg border border-neutral-dark px-3 py-2 text-left text-neutral-light" onClick={() => {
                    setQ("");
                    if (step === "region") { setRegion(name); setStep("city"); }
                    else if (step === "city") { setCity(name); setStep("isp"); }
                    else { setIsp(name); setStep("rotation"); }
                  }}>{name}</button>
                ))}
              </div>
            </>
          )}
          {step === "rotation" && (
            <div className="grid gap-2">
              {rotations.map((r) => (
                <button key={r.value} className="rounded-lg border border-neutral-dark px-3 py-2 text-left text-neutral-light" onClick={() => { setRotation(r.value); setStep("review"); }}>{r.label}</button>
              ))}
            </div>
          )}
          {step === "review" && (
            <div className="rounded-2xl border border-neutral-dark p-6 space-y-2 text-neutral-light">
              <p>{flag(country)} {countryName} {region} {city} {isp}</p>
              <p>Rotation: {rotation < 0 ? "Sticky" : `${rotation}s`}</p>
              <button disabled={acting} className="mt-4 rounded-xl bg-brand-accent text-neutral-dark px-4 py-2 font-semibold" onClick={async () => {
                try {
                  if ((window as any).__gbMoveMode) {
                    const res = await proxyAction({ proxyId: String(proxyId), action: "move", body: { country, regionName: region || undefined, city: city || undefined, isp: isp || undefined } }).unwrap();
                    toast.success("Location updated");
                    setCurrentProxy((res as any)?.data?.proxy);
                    (window as any).__gbMoveMode = false;
                    setHub("proxy");
                  } else {
                    const res = await packageAction({ packageId: String(packageId), action: "create-proxy", body: { country, regionName: region || undefined, city: city || undefined, isp: isp || undefined, gbRotation: rotation } }).unwrap();
                    toast.success("Proxy created");
                    setCurrentProxy((res as any)?.data?.proxy);
                    setProxyId(String((res as any)?.data?.proxy?._id));
                    setHub("proxy");
                  }
                } catch (err: any) { toast.error(err?.data?.message || "Create failed"); }
              }}>{acting ? <ButtonSpinner /> : "Confirm create"}</button>
            </div>
          )}
        </div>
      )}

      {hub === "proxies" && (
        <div className="space-y-3">
          {packageProxies.length === 0 && <p className="text-neutral-medium">No proxies in this package.</p>}
          {packageProxies.map((p) => (
            <button key={p._id} className="w-full rounded-xl border border-neutral-dark p-4 text-left text-neutral-light" onClick={() => openProxyDetails(p)}>
              {flag(p.location)} {p.ipAddress}:{p.portDisplay || "10000 - 10999"}
            </button>
          ))}
        </div>
      )}

      {hub === "proxy" && currentProxy && (
        <div className="rounded-2xl border border-neutral-dark p-6 space-y-3 text-neutral-light">
          <p>Host: <code>{currentProxy.ipAddress}</code></p>
          <p>Port: <code>{currentProxy.portDisplay || "10000 - 10999"}</code></p>
          {currentProxy.username && <p>User: <code>{currentProxy.username}</code></p>}
          {currentProxy.password && <p>Pass: <code>{currentProxy.password}</code></p>}
          <p>Location: {flag(currentProxy.location)} {currentProxy.location}{currentProxy.city ? ` / ${currentProxy.city}` : ""}</p>
          {currentProxy.isp && <p>ISP: {currentProxy.isp}</p>}
          <p>Rotation: {Number(currentProxy.gbRotation) < 0 ? "Sticky" : `${currentProxy.gbRotation}s`}</p>
          <p className="text-sm text-neutral-medium">Created: {currentProxy.purchasedAt || currentProxy.createdAt ? new Date(currentProxy.purchasedAt || currentProxy.createdAt).toLocaleString() : "-"}</p>
          <input value={proxyTitle} onChange={(e) => setProxyTitle(e.target.value.slice(0, 40))} className="w-full rounded-lg bg-neutral-dark/60 border border-neutral-dark px-3 py-2" placeholder="Proxy name" />
          <select value={proxyRotation} onChange={(e) => setProxyRotation(Number(e.target.value))} className="w-full rounded-lg bg-neutral-dark/60 border border-neutral-dark px-3 py-2">
            {rotations.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <div className="flex flex-wrap gap-2">
            <button disabled={proxyActing} className="rounded-lg border border-neutral-dark px-3 py-2" onClick={async () => {
              try { const res = await proxyAction({ proxyId: String(proxyId), action: "modify", body: { title: proxyTitle, gbRotation: proxyRotation } }).unwrap(); setCurrentProxy((res as any)?.data?.proxy); toast.success("Updated"); } catch (err: any) { toast.error(err?.data?.message || "Update failed"); }
            }}>Save</button>
            <button className="rounded-lg border border-neutral-dark px-3 py-2" onClick={() => { setStep("country"); setCountry(""); setRegion(""); setCity(""); setIsp(""); setQ(""); setHub("create"); (window as any).__gbMoveMode = true; }}>Change location / IP</button>
            <button disabled={proxyActing} className="rounded-lg border border-red-500/40 px-3 py-2" onClick={async () => {
              if (!window.confirm("Delete this proxy?")) return;
              try { await proxyAction({ proxyId: String(proxyId), action: "delete" }).unwrap(); toast.success("Deleted"); loadProxies(String(packageId)); } catch (err: any) { toast.error(err?.data?.message || "Delete failed"); }
            }}>Delete</button>
          </div>
        </div>
      )}
    </div>
  );
}
// 2026-10-14T10:28:23+00:00
